import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Row, Col, Form, Button, Card } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faDownload, faFilter, faCalendar } from '@fortawesome/free-solid-svg-icons';
import Select from "react-select";
import Loader from "../../components/common/Loader";
import HeaderCard from "../../components/common/HeaderCard";
import BackButton from "../../components/common/BackButton";
import SecCard from "../../components/common/SecCard";
import { SERVER_ORIGIN } from "../../utilities/constants";
import { hasReadOnlyAccess } from "../../utilities/helper_functions";
import css from "../../css/admin/users-page.module.css";

const SingleMouReport = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [mouData, setMouData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const navigate = useNavigate();
    const isReadOnly = hasReadOnlyAccess();
    // Filter states
    const [filters, setFilters] = useState({
        region: '',
        chapter: '',
        mouStatus: '',
        dateFrom: '',
        dateTo: '',
        search: ''
    });

    // Options for dropdowns
    const [regionOptions, setRegionOptions] = useState([]);
    const [chapterOptions, setChapterOptions] = useState([]);

    useEffect(() => {
        loadRegionOptions();
        loadChapterOptions();
        fetchMouData();
    }, [filters]);

    const loadRegionOptions = async () => {
        try {
            const adminId = process.env.REACT_APP_ADMIN_ID;
            const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
            const basicAuth = btoa(`${adminId}:${adminPassword}`);

            const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/regions/all`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${basicAuth}`,
                    "auth-token": sessionStorage.getItem("token"),
                },
            });

            if (response.ok) {
                const result = await response.json();
                const options = result.allRegions?.map(region => ({
                    value: region.name,
                    label: region.name
                })) || [];
                setRegionOptions(options);
            } else {
                console.error("Region API error:", response.status, response.statusText);
            }
        } catch (error) {
            console.error("Error loading regions:", error);
        }
    };

    const loadChapterOptions = async () => {
        try {
            const adminId = process.env.REACT_APP_ADMIN_ID;
            const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
            const basicAuth = btoa(`${adminId}:${adminPassword}`);

            const response = await fetch(`${SERVER_ORIGIN}/api/admin/auth/chapters/all`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${basicAuth}`,
                    "auth-token": sessionStorage.getItem("token"),
                },
            });

            if (response.ok) {
                const result = await response.json();
                const options = result.allChapters?.map(chapter => ({
                    value: chapter.chapter_name,
                    label: chapter.chapter_name
                })) || [];
                setChapterOptions(options);
            } else {
                console.error("Chapter API error:", response.status, response.statusText);
            }
        } catch (error) {
            console.error("Error loading chapters:", error);
        }
    };

    const fetchMouData = async () => {
        setIsLoading(true);
        try {
            const adminId = process.env.REACT_APP_ADMIN_ID;
            const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
            const basicAuth = btoa(`${adminId}:${adminPassword}`);

            const queryParams = new URLSearchParams({
                limit: '1000', // Get all data for client-side filtering
                ...filters
            }).toString();

            const response = await fetch(
                `${SERVER_ORIGIN}/api/admin/auth/mou-report?${queryParams}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${basicAuth}`,
                        "auth-token": sessionStorage.getItem("token"),
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                setMouData(result.data || []);
            } else if (response.status === 401) {
                navigate("/admin/login");
            } else {
                toast.error("Failed to load MoU data");
            }
        } catch (error) {
            console.error("Error fetching MoU data:", error);
            toast.error("Error loading data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            region: '',
            chapter: '',
            mouStatus: '',
            dateFrom: '',
            dateTo: '',
            search: ''
        });
        setSearchQuery("");
    };

    const downloadCSV = () => {
        try {
            const csvHeaders = [
                'S.NO',
                'REGION',
                'CHAPTER', 
                'NAME OF THE SCHOOL',
                'Date of Signing',
                'Date of Validity',
                'NUMBER OF THALIR STUDENT'
            ];

            const csvData = mouData.map((item, index) => [
                index + 1,
                item.region || 'N/A',
                item.chapterName || 'N/A',
                item.name || 'N/A',
                item.allMouUrl?.[0]?.signDate ? new Date(item.allMouUrl[0].signDate).toLocaleDateString() : 'N/A',
                item.mouExpiryDate ? new Date(item.mouExpiryDate).toLocaleDateString() : 'N/A',
                item.studentCount || 0
            ]);

            const csvContent = [csvHeaders, ...csvData]
                .map(row => row.map(field => `"${field}"`).join(','))
                .join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `single-mou-report-${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("CSV downloaded successfully!");
            }
        } catch (error) {
            console.error("Error downloading CSV:", error);
            toast.error("Failed to download CSV");
        }
    };

    const columns = [
        {
            field: 'id',
            headerName: 'S.NO',
            width: 70,
            renderCell: (params) => {
                return params.api.getRowIndexRelativeToVisibleRows(params.id) + 1;
            }
        },
        {
            field: 'region',
            headerName: 'REGION',
            width: 120,
            renderCell: (params) => params.row.region || 'N/A'
        },
        {
            field: 'chapterName',
            headerName: 'CHAPTER',
            width: 180,
            renderCell: (params) => params.row.chapterName || 'N/A'
        },
        {
            field: 'name',
            headerName: 'NAME OF THE SCHOOL',
            width: 300,
            renderCell: (params) => params.row.name || 'N/A'
        },
        {
            field: 'signDate',
            headerName: 'Date of Signing',
            width: 140,
            renderCell: (params) => {
                const signDate = params.row.allMouUrl?.[0]?.signDate;
                return signDate ? new Date(signDate).toLocaleDateString() : 'N/A';
            }
        },
        {
            field: 'mouExpiryDate',
            headerName: 'Date of Validity',
            width: 140,
            renderCell: (params) => {
                return params.row.mouExpiryDate 
                    ? new Date(params.row.mouExpiryDate).toLocaleDateString() 
                    : 'N/A';
            }
        },
        {
            field: 'studentCount',
            headerName: 'NUMBER OF THALIR STUDENT',
            width: 200,
            renderCell: (params) => params.row.studentCount || 0
        }
    ];

    const rows = mouData.map((item, index) => ({
        id: item._id || index,
        ...item
    }));

    return (
        <div>
            {isLoading && <Loader />}
            
            <HeaderCard>
                <BackButton />
                <h1 className="allTitles">Single MoU Report</h1>
                <p className="allDescs">
                    View and export detailed MoU information for all institutions
                </p>
            </HeaderCard>

            {/* Filters Section */}
            <SecCard>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        Filters
                    </h4>
                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                        Clear All
                    </Button>
                </div>
                
                <Row>
                    <Col md={3} className="mb-3">
                        <Form.Group>
                            <Form.Label>Search School</Form.Label>
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    placeholder="Search by school name..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                />
                                <FontAwesomeIcon 
                                    icon={faMagnifyingGlass} 
                                    className="position-absolute"
                                    style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                    
                    <Col md={3} className="mb-3">
                        <Form.Group>
                            <Form.Label>Region</Form.Label>
                            <Select
                                value={regionOptions.find(opt => opt.value === filters.region)}
                                onChange={(selected) => handleFilterChange('region', selected?.value || '')}
                                options={regionOptions}
                                placeholder="Select Region..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => "No regions found"}
                            />
                        </Form.Group>
                    </Col>
                    
                    <Col md={3} className="mb-3">
                        <Form.Group>
                            <Form.Label>Chapter</Form.Label>
                            <Select
                                value={chapterOptions.find(opt => opt.value === filters.chapter)}
                                onChange={(selected) => handleFilterChange('chapter', selected?.value || '')}
                                options={chapterOptions}
                                placeholder="Select Chapter..."
                                isClearable
                                isSearchable
                                noOptionsMessage={() => "No chapters found"}
                            />
                        </Form.Group>
                    </Col>
                    
                    <Col md={3} className="mb-3">
                        <Form.Group>
                            <Form.Label>MOU Status</Form.Label>
                            <Form.Select
                                value={filters.mouStatus}
                                onChange={(e) => handleFilterChange('mouStatus', e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="submitted">Submitted</option>
                                <option value="rejected">Rejected</option>
                                <option value="expired">Expired</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group>
                            <Form.Label>Date From</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                        <Form.Group>
                            <Form.Label>Date To</Form.Label>
                            <Form.Control
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </SecCard>

            {/* Data Table Section */}
            <SecCard>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>MoU Report Data ({mouData.length} records)</h4>
                    <Button 
                        variant="success" 
                        onClick={downloadCSV}
                        disabled={mouData.length === 0}
                        title={`Download all data (${mouData.length} records)`}
                    >
                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                        Download CSV
                    </Button>
                </div>
                
                {isLoading ? (
                    <Loader />
                ) : (
                    <div style={{ height: 600, width: '100%' }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize:10, page: 0 },
                                },
                            }}
                            slots={{ toolbar: GridToolbar }}
                            disableRowSelectionOnClick
                        />
                    </div>
                )}
            </SecCard>
        </div>
    );
};

export default SingleMouReport; 